import { useMemo, useRef, useState } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

// Reusable tag/chip input. Type to filter existing tags, Enter to add/select,
// × to remove. Keyboard + mobile friendly. Value is a string[] (schema unchanged).
export function TagInput({ value = [], onChange, suggestions = [], placeholder = "Add tag", testid }) {
  const [q, setQ] = useState("");
  const inputRef = useRef(null);
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return suggestions.filter((s) => !value.includes(s) && (!t || s.toLowerCase().includes(t))).slice(0, 6);
  }, [q, suggestions, value]);

  const add = (raw) => {
    const v = (raw ?? q).trim();
    if (!v || value.includes(v)) { setQ(""); return; }
    onChange([...value, v]); setQ("");
  };
  const remove = (v) => onChange(value.filter((x) => x !== v));

  const onKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); }
    else if (e.key === "Backspace" && !q && value.length) remove(value[value.length - 1]);
  };

  return (
    <div className="relative">
      <div onClick={() => inputRef.current?.focus()}
        className="flex min-h-10 flex-wrap items-center gap-1.5 rounded-lg border border-input bg-background px-2 py-1.5 focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-0"
        data-testid={testid}>
        {value.map((t) => (
          <span key={t} className="inline-flex items-center gap-1 rounded-md bg-accent-subtle px-2 py-0.5 text-xs font-medium text-accent" data-testid={`tag-chip-${t}`}>
            {t}
            <button type="button" onClick={(e) => { e.stopPropagation(); remove(t); }} aria-label={`Remove ${t}`} className="text-accent/70 hover:text-accent"><X className="h-3 w-3" /></button>
          </span>
        ))}
        <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKeyDown}
          placeholder={value.length ? "" : placeholder} data-testid={testid ? `${testid}-field` : undefined}
          className="min-w-[80px] flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground/50" />
      </div>
      {q.trim() && (filtered.length > 0 || !value.includes(q.trim())) && (
        <div className="absolute z-10 mt-1 w-full overflow-hidden rounded-lg border border-border bg-popover shadow-lift">
          {filtered.map((s) => (
            <button key={s} type="button" onClick={() => add(s)} className={cn("block w-full px-3 py-1.5 text-left text-sm hover:bg-muted")} data-testid={`tag-suggest-${s}`}>{s}</button>
          ))}
          {!suggestions.includes(q.trim()) && !value.includes(q.trim()) && (
            <button type="button" onClick={() => add()} className="block w-full px-3 py-1.5 text-left text-sm text-accent hover:bg-muted">Create “{q.trim()}”</button>
          )}
        </div>
      )}
    </div>
  );
}
